import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { atom, useAtom } from "jotai";
import { useCallback, useMemo, useState } from "react";
import type {
    EmissioneventsListDataSourceEnum,
    EmissionRecordsListRequest,
    EmissionRecordView,
    Site,
} from "../../apiClient/generated";
import { useUser } from "../../auth";
import {
    useEmissionEventsApiClient,
    useEmissionRecordsApiClient,
} from "../../hooks";
import { SearchInput } from "../../ui/Inputs";
import { formatDate, formatDateTime } from "../../utils/dates";
import { DataTableV3UI, useDataTableV3 } from "../DataTable";
import {
    ClearFiltersButton,
    SEARCH_FILTER_KEY,
    useActiveFilters,
} from "../DataTable/ClearFiltersButton";
import { TableSettingsModal, TableSortModal } from "../DataTable/Extra";
import {
    LocationColumnModeToggle,
    LocationDisplay,
} from "../DataTable/LocationColumn";
import { type LocationFilterState, tableStateFamily } from "../DataTable/state";
import {
    AssignedToFilter,
    DataProviderFilterV2,
    DateRangeFilter,
    EventGlobalStatusFilter,
    LocationFilterV2,
    RangeFilter,
} from "../DataTable/TableFilters";
import { BulkActions } from "./BulkActions";
import { RateWithUncertaintyCell } from "./Extras";

const configurableColumns = [
    { id: "eventStatus", name: "Status" },
    { id: "detectedRate", name: "Detection rate" },
    { id: "concentration", name: "Concentration" },
    { id: "infrastructure", name: "Infrastructure" },
    { id: "provider", name: "Provider" },
    { id: "responsible", name: "Assignee" },
    { id: "detectionTimestamp", name: "Detection Date (UTC)" },
    { id: "createdAt", name: "Match Date" },
    { id: "eventUpdatedAt", name: "Last Updated" },
    { id: "dataPointCreatedAt", name: "Import Date" },
    { id: "location", name: "Location" },
    { id: "operatorIds", name: "Operator Unique ID" },
    { id: "bridgerEquipmentId", name: "Bridger Equipment ID" },
    { id: "bridgerEquipmentType", name: "Bridger Equipment Type" },
    { id: "bridgerEmissionLocationId", name: "Bridger Emission Location ID" },
    { id: "bridgerDetectionId", name: "Bridger Detection ID" },
    { id: "bridgerPersistence", name: "Bridger Persistence" },
    { id: "bridgerEmitterHeightFt", name: "Bridger Emitter Height (ft)" },
];

export const emissionTableFiltersAtom = atom<EmissionRecordsListRequest>({});

const columnHelper = createColumnHelper<EmissionRecordView>();
interface ApiDataTable<T> {
    dataSource?: EmissioneventsListDataSourceEnum[];
    onClickRow?: (id: string | number, object: T) => void;
}

interface SiteNameColumnValueProps {
    emission: EmissionRecordView;
}

const SiteNameColumnValue = (props: SiteNameColumnValueProps) => {
    const eventsApiClient = useEmissionEventsApiClient();
    const emissionsApiClient = useEmissionRecordsApiClient();
    const isLinked =
        props.emission.mainEvent.eventStatus === "LINKED" &&
        !!props.emission.mainEvent.mainEvent;

    const mainEmissionQuery = useQuery({
        queryKey: [
            "emissionRecordForEvent",
            props.emission.mainEvent.mainEvent,
        ],
        queryFn: async () => {
            const mainEvent = await eventsApiClient.emissioneventsRetrieve({
                id: props.emission.mainEvent.mainEvent,
            });
            const response = await emissionsApiClient.emissionRecordsRetrieve({
                id: mainEvent.mainEmissionRecord,
            });
            return response;
        },
        enabled: isLinked,
    });

    const renderMultipleMatches = (matches: Site[]) => (
        <span
            className="flex gap-1 items-center"
            title={`Multiple sites matched with this emission: ${matches
                .map((i) => i.siteName)
                .join(", ")}.`}
        >
            Multiple Matches
            <QuestionMarkCircleIcon className="h-4 w-4" />
        </span>
    );

    // For linked events, the relevant data comes from the queried main emission.
    // Otherwise, use the emission on this row directly.
    const source = isLinked ? mainEmissionQuery.data : props.emission;
    return (
        <div
            className="max-w-52 overflow-hidden truncate"
            title={
                isLinked && mainEmissionQuery.isLoading
                    ? "Loading…"
                    : source?.site
                      ? source.site.siteName
                      : source?.siteMatches && source.siteMatches.length > 1
                        ? `Multiple sites matched with this emission: ${source.siteMatches
                              .map((i) => i.siteName)
                              .join(", ")}.`
                        : "No associated infrastructure."
            }
        >
            {isLinked && mainEmissionQuery.isLoading ? (
                <span className="italic text-gray-400">Loading…</span>
            ) : source?.site ? (
                source.site.siteName
            ) : source?.siteMatches && source.siteMatches.length > 1 ? (
                renderMultipleMatches(source.siteMatches)
            ) : (
                <span className="italic">No associated infrastructure.</span>
            )}
        </div>
    );
};

export const EmissionApiDataTableV2 = (
    props: ApiDataTable<EmissionRecordView>,
) => {
    const { dataSource } = props;
    const apiClient = useEmissionRecordsApiClient();
    const [filterSearch, setFilterSearch] = useState("");
    const debouncedSearch = useDebounce(filterSearch, 600);
    const { id: userId, settings } = useUser();
    const defaultSelfFilter = settings.emissionTableDefaultFilterAssignedToSelf;

    const baseEmissionColumns = useMemo(
        () => [
            columnHelper.accessor(
                (row) => ({
                    eventStatus: row.mainEvent.eventStatus,
                    mainEventStatus: row.mainEvent.mainEventStatus,
                    linkedEvents: row.mainEvent.linkedEvents,
                }),
                {
                    id: "eventStatus",
                    meta: {
                        customFilterWidget: EventGlobalStatusFilter,
                        sortingKey: "main_event__event_status",
                    },
                    cell: (info) => {
                        const { eventStatus, mainEventStatus, linkedEvents } =
                            info.getValue();
                        if (eventStatus === "LINKED") {
                            return (
                                <div className="capitalize">
                                    <span className="">Linked</span>{" "}
                                    <p className="italic text-xs whitespace-nowrap">
                                        (
                                        {mainEventStatus
                                            ?.replaceAll("_", " ")
                                            .toLowerCase()}
                                        )
                                    </p>
                                </div>
                            );
                        }
                        return (
                            <span className="capitalize whitespace-nowrap">
                                {eventStatus.replaceAll("_", " ").toLowerCase()}
                                {linkedEvents?.length > 0 &&
                                    ` (${linkedEvents.length} linked)`}
                            </span>
                        );
                    },
                    header: () => "Status",
                },
            ),
            columnHelper.accessor(
                (row) => {
                    return {
                        detectedRate: row.dataPoint?.detectedRate,
                        detectedRateUncertainty:
                            row.dataPoint?.detectedRateUncertainty,
                    };
                },
                {
                    id: "detectedRate",
                    meta: {
                        customFilterWidget: RangeFilter,
                        sortingKey: "data_point__detected_rate",
                    },
                    cell: (info) => {
                        const { detectedRate, detectedRateUncertainty } =
                            info.getValue();
                        return (
                            <RateWithUncertaintyCell
                                rate={
                                    detectedRate
                                        ? detectedRate / 1000
                                        : detectedRate
                                }
                                uncertainty={
                                    detectedRateUncertainty
                                        ? detectedRateUncertainty / 1000
                                        : detectedRateUncertainty
                                }
                                unit="kg/h"
                            />
                        );
                    },
                    header: () => "Detection rate",
                },
            ),
            columnHelper.accessor(
                (row) => {
                    return {
                        concentration: row.dataPoint?.concentration,
                        concentrationUncertainty:
                            row.dataPoint?.concentrationUncertainty,
                    };
                },
                {
                    id: "concentration",
                    meta: {
                        customFilterWidget: RangeFilter,
                        sortingKey: "data_point__concentration",
                    },
                    cell: (info) => {
                        const { concentration, concentrationUncertainty } =
                            info.getValue();
                        return (
                            <RateWithUncertaintyCell
                                rate={concentration}
                                uncertainty={concentrationUncertainty}
                                unit="ppm*m"
                            />
                        );
                    },
                    header: () => "Concentration",
                },
            ),
            columnHelper.accessor((row) => row, {
                id: "infrastructure",
                cell: (info) => {
                    const value = info.getValue();
                    return <SiteNameColumnValue emission={value} />;
                },
                header: () => "Infrastructure",
                enableColumnFilter: true,
                enableSorting: true,
                meta: {
                    sortingKey: "site__site_name",
                    filteringKey: "relatedSiteName",
                },
            }),
            columnHelper.accessor(
                (row) => ({
                    providerName: row.providerName,
                    secondaryDataSource: row.dataPoint?.secondaryDataSource,
                }),
                {
                    id: "provider",
                    cell: (info) => {
                        const value = info.getValue();
                        return `${value.providerName} ${
                            value.secondaryDataSource?.trim() !== ""
                                ? "(" + value.secondaryDataSource + ")"
                                : ""
                        }`;
                    },
                    header: () => "Provider",
                    enableSorting: false,
                    meta: {
                        customFilterWidget: DataProviderFilterV2,
                        extraFilterProps: {
                            dataSource: props.dataSource,
                            hasData: true,
                        },
                    },
                },
            ),
            columnHelper.accessor((row) => row.dataPoint?.detectionTimestamp, {
                id: "detectionTimestamp",
                meta: {
                    sortingKey: "data_point__detection_timestamp",
                    filteringKey: "detectionDateRange",
                    customFilterWidget: DateRangeFilter,
                },
                cell: (info) => formatDateTime(info.renderValue()),
                header: () => "Detection Date (UTC)",
                enableColumnFilter: true,
            }),
            columnHelper.accessor((row) => row.mainEvent.responsibleName, {
                id: "responsible",
                meta: {
                    filteringKey: "assignedTo",
                    customFilterWidget: AssignedToFilter,
                },
                cell: (info) => info.getValue(),
                header: () => "Assignee",
                enableColumnFilter: true,
                enableSorting: false,
            }),
            columnHelper.accessor("createdAt", {
                id: "createdAt",
                meta: {
                    customFilterWidget: DateRangeFilter,
                    sortingKey: "created_at__date",
                },
                cell: (info) => {
                    if (!info.getValue()) {
                        return "-";
                    }
                    return formatDate(info.getValue());
                },
                header: () => "Match Date",
            }),
            columnHelper.accessor((row) => row.mainEvent.updatedAt, {
                id: "eventUpdatedAt",
                meta: {
                    customFilterWidget: DateRangeFilter,
                    sortingKey: "main_event__updated_at",
                },
                cell: (info) => {
                    if (!info.getValue()) {
                        return "-";
                    }
                    return formatDateTime(info.getValue());
                },
                header: () => "Last Updated",
                enableColumnFilter: true,
            }),
            columnHelper.accessor((row) => row.dataPoint?.createdAt, {
                id: "dataPointCreatedAt",
                meta: {
                    customFilterWidget: DateRangeFilter,
                    sortingKey: "data_point__created_at__date",
                },
                cell: (info) => {
                    if (!info.getValue()) {
                        return "-";
                    }
                    return formatDate(info.getValue());
                },
                header: () => "Import Date",
                enableColumnFilter: true,
            }),
            columnHelper.accessor(
                (row) => ({
                    location: row.dataPoint?.location,
                    presetIds: row.presetIds,
                }),
                {
                    id: "location",
                    cell: (info) => {
                        const { location: value, presetIds } = info.getValue();
                        if (!value) {
                            return "";
                        }
                        const locationFilter = info.table.options.meta?.filters
                            ?.location as LocationFilterState | undefined;
                        return (
                            <LocationDisplay
                                location={value.coordinates}
                                presets={presetIds}
                                selectedPresets={locationFilter?.publicPresets}
                                mapLinkParams={{
                                    emissionId: info.row.original.id,
                                    lat: value.coordinates[1].toString(),
                                    lon: value.coordinates[0].toString(),
                                }}
                                allowCopy
                            />
                        );
                    },
                    header: () => (
                        <div className="min-w-36 flex items-center justify-start gap-2">
                            Location
                            <LocationColumnModeToggle />
                        </div>
                    ),
                    enableColumnFilter: true,
                    enableSorting: false,
                    meta: {
                        label: "Location",
                        customFilterWidget: LocationFilterV2,
                    },
                },
            ),
            columnHelper.accessor(
                (row) => ({
                    siteMatches: row.siteMatches,
                    site: row.site,
                }),
                {
                    id: "operatorIds",
                    cell: (info) => {
                        const data = info.getValue();
                        if (data.site) {
                            return data.site.operatorUniqueId;
                        }

                        if (Array.isArray(data.siteMatches)) {
                            const infraWithOperatorIds =
                                data.siteMatches.filter(
                                    (i) => i.operatorUniqueId.trim() !== "",
                                );
                            if (infraWithOperatorIds.length > 1) {
                                return (
                                    <span
                                        className="flex gap-1 items-center italic text-gray-500"
                                        title={infraWithOperatorIds
                                            .map((i) => i.operatorUniqueId)
                                            .join(", ")}
                                    >
                                        Multiple
                                        <QuestionMarkCircleIcon className="h-4" />
                                    </span>
                                );
                            }
                            if (
                                infraWithOperatorIds.length === 1 &&
                                infraWithOperatorIds[0].operatorUniqueId.trim() !==
                                    ""
                            ) {
                                return infraWithOperatorIds[0].operatorUniqueId;
                            }
                        }
                        return "";
                    },
                    header: () => "Operator Unique ID",
                    enableColumnFilter: false,
                    enableSorting: false,
                },
            ),
            columnHelper.accessor(
                (row) => row.dataPoint?.data?.bridger_equipment_id,
                {
                    id: "bridgerEquipmentId",
                    cell: (info) => info.getValue() ?? "-",
                    header: () => "Bridger Equipment ID",
                    enableColumnFilter: true,
                    enableSorting: true,
                    meta: {
                        filteringKey: "bridgerEquipmentId",
                        sortingKey: "data_point__data__bridger_equipment_id",
                    },
                },
            ),
            columnHelper.accessor(
                (row) => row.dataPoint?.data?.bridger_equipment_type,
                {
                    id: "bridgerEquipmentType",
                    cell: (info) => info.getValue() ?? "-",
                    header: () => "Bridger Equipment Type",
                    enableColumnFilter: true,
                    enableSorting: true,
                    meta: {
                        filteringKey: "bridgerEquipmentType",
                        sortingKey: "data_point__data__bridger_equipment_type",
                    },
                },
            ),
            columnHelper.accessor(
                (row) => row.dataPoint?.data?.bridger_emission_location_id,
                {
                    id: "bridgerEmissionLocationId",
                    cell: (info) => info.getValue() ?? "-",
                    header: () => "Bridger Emission Location ID",
                    enableColumnFilter: true,
                    enableSorting: true,
                    meta: {
                        filteringKey: "bridgerEmissionLocationId",
                        sortingKey:
                            "data_point__data__bridger_emission_location_id",
                    },
                },
            ),
            columnHelper.accessor(
                (row) => row.dataPoint?.data?.bridger_detection_id,
                {
                    id: "bridgerDetectionId",
                    cell: (info) => info.getValue() ?? "-",
                    header: () => "Bridger Detection ID",
                    enableColumnFilter: true,
                    enableSorting: true,
                    meta: {
                        filteringKey: "bridgerDetectionId",
                        sortingKey: "data_point__data__bridger_detection_id",
                    },
                },
            ),
            columnHelper.accessor(
                (row) => row.dataPoint?.data?.bridger_persistence,
                {
                    id: "bridgerPersistence",
                    cell: (info) => info.getValue() ?? "-",
                    header: () => "Bridger Persistence",
                    enableColumnFilter: true,
                    enableSorting: true,
                    meta: {
                        filteringKey: "bridgerPersistence",
                        sortingKey: "data_point__data__bridger_persistence",
                    },
                },
            ),
            columnHelper.accessor(
                (row) => row.dataPoint?.data?.["emitter_height_(ft)"],
                {
                    id: "bridgerEmitterHeightFt",
                    cell: (info) => info.getValue() ?? "-",
                    header: () => "Bridger Emitter Height (ft)",
                    enableColumnFilter: true,
                    enableSorting: true,
                    meta: {
                        customFilterWidget: RangeFilter,
                        filteringKey: "bridgerEmitterHeightFt",
                        sortingKey: "data_point__data__emitter_height_(ft)",
                    },
                },
            ),
        ],
        [props.dataSource],
    );

    // Config is shared across the data-source tabs (shared configId); each tab
    // has its own tableId so pagination/selection don't carry across tabs.
    const CONFIG_ID = "main-emissions-table";
    const tableId = `main-emissions-table-${
        dataSource?.length ? dataSource.join("-") : "all"
    }`;

    // Table state
    const tableAtom = useMemo(
        () =>
            tableStateFamily({
                tableId,
                configId: CONFIG_ID,
                initialState: {
                    sorting: [
                        {
                            id: "createdAt",
                            desc: true,
                        },
                        {
                            id: "detectedRate",
                            desc: true,
                        },
                    ],
                    filters: {
                        eventStatus: [
                            "COMPLETED",
                            "CREATED",
                            "NEW",
                            "REJECTED",
                            "WAITING_APPROVAL",
                            "WORK_IN_PROGRESS",
                        ],
                        assignedTo: defaultSelfFilter
                            ? [`${userId}`]
                            : undefined,
                    },
                    columnVisibility: {
                        eventUpdatedAt: false,
                        dataPointCreatedAt: false,
                        concentration: false,
                        bridgerEquipmentId: false,
                        bridgerEquipmentType: false,
                        bridgerEmissionLocationId: false,
                        bridgerDetectionId: false,
                        bridgerPersistence: false,
                        bridgerEmitterHeightFt: false,
                    },
                },
            }),
        [tableId, defaultSelfFilter, userId],
    );

    // Custom reset that clears filters
    const [tableState, setTableState] = useAtom(tableAtom);
    const [filters, setFilters] = useAtom(emissionTableFiltersAtom);
    const resetTableState = useCallback(() => {
        setTableState((ts) => ({
            ...ts,
            sorting: [
                {
                    id: "createdAt",
                    desc: true,
                },
                {
                    id: "detectedRate",
                    desc: true,
                },
            ],
            filters: {},
            rowSelection: {},
            pagination: { ...ts.pagination, pageIndex: 0 },
        }));
        setFilters({});
    }, [setTableState, setFilters]);

    // Filters applied to table -> should also be applied to
    // stats, and anything else needed
    const fetchFunction = async (props: any) => {
        // Update filters variable every time the table makes a request.
        setFilters({
            ...props,
            page: undefined,
            pageSize: undefined,
        });
        return await apiClient.emissionRecordsList(props);
    };

    const { table, dataQuery, dataName } = useDataTableV3({
        dataName: tableId,
        configId: CONFIG_ID,
        columns: baseEmissionColumns,
        extraFilters: useMemo(
            () => ({
                search: debouncedSearch,
                dataSource,
            }),
            [debouncedSearch, dataSource],
        ),
        fetchFunction: fetchFunction,
        sortable: true,
        filterable: true,
        selectable: true,
        getRowId: (e) => e.id,
    });

    const selectedRows = useMemo(() => {
        return {
            allSelected: table.getIsAllPageRowsSelected(),
            selectedIds: Object.entries(tableState.rowSelection)
                .filter(([, selected]) => selected)
                .map(([id]) => id),
        };
    }, [table, tableState.rowSelection]);

    const activeFilters = useActiveFilters({
        columns: baseEmissionColumns,
        filters: tableState.filters,
        search: filterSearch,
    });

    // Clear a single filter, mirroring resetTableState but for one key.
    const clearFilter = useCallback(
        (key: string) => {
            if (key === SEARCH_FILTER_KEY) {
                setFilterSearch("");
                return;
            }
            setTableState((ts) => ({
                ...ts,
                filters: { ...ts.filters, [key]: undefined },
                pagination: { ...ts.pagination, pageIndex: 0 },
            }));
        },
        [setTableState],
    );

    return (
        <div className="flex-1 flex flex-col">
            {/* Search bar */}
            <div className="py-3 px-4 flex items-center text-sm justify-between border-b">
                <SearchInput
                    containerClassName="w-96"
                    placeholder="Search by Emission ID or site name..."
                    value={filterSearch}
                    onChange={(newValue) => setFilterSearch(newValue)}
                />
                <div className="flex gap-2 items-center">
                    <BulkActions
                        filters={filters}
                        selectedIds={selectedRows.selectedIds}
                        allSelected={selectedRows.allSelected}
                        onFinalizeAction={() =>
                            setTableState((ts) => ({
                                ...ts,
                                // Delect items after bulk actions finishes.
                                rowSelection: {},
                            }))
                        }
                    />
                    <ClearFiltersButton
                        activeFilters={activeFilters}
                        onClearFilter={clearFilter}
                        onClearAll={resetTableState}
                    />
                    <TableSortModal<EmissionRecordView>
                        columns={baseEmissionColumns}
                        sortingState={tableState.sorting}
                        setSortingState={(newSorting) =>
                            setTableState((ts) => {
                                return {
                                    ...ts,
                                    sorting: newSorting,
                                };
                            })
                        }
                    />
                    <TableSettingsModal
                        columnVisibility={tableState.columnVisibility}
                        setColumnVisibility={(newStates) => {
                            setTableState((ts) => {
                                return {
                                    ...ts,
                                    columnVisibility: newStates,
                                };
                            });
                        }}
                        configurableColumns={configurableColumns}
                    />
                </div>
            </div>

            {/* Data table */}
            <DataTableV3UI
                table={table}
                dataName={dataName}
                columns={baseEmissionColumns}
                loadingData={dataQuery.isFetching}
                itemCount={dataQuery.data?.count || 0}
                onClickRow={props.onClickRow}
            />
        </div>
    );
};
